import {AddBuildingCommand} from "../../commands/addBuilding.command";
import {RemoveBuildingCommand} from "../../commands/removeBuilding.command";
import {UpdateBuildingCommand} from "../../commands/updateBuilding.command";
import {useIntegrator} from "../../integrator.context";
import {AddBuildingAttributeCommand} from "../../commands/addBuildingAttribute.command";
import {RemoveBuildingAttributeCommand} from "../../commands/removeBuildingAttribute.command";
import {Metadata, TriggerKinds, TriggerSpec} from "../../models/remoteConstants.model";
import {AddBuildingPassiveRewardCommand} from "../../commands/addBuildingPassiveReward.command";
import PassiveRewardCard from "./passiveRewardCard";
import ConstructionImages from "./constructionImages";
import ResourceVariants from "./resourceVariants";
import {DropTargetMonitor, useDrag, useDrop} from "react-dnd";
import LocalizationInput from "../../elements/localizationInput.element";
import LocalizationTextArea from "../../elements/localizationTextArea.element";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import MetadataLabel from "../../elements/metadataLabel.element";
import EntityLabel from "../../elements/entityLabel";
import BooleanInput from "../../elements/booleanInput.element";
import IconButton from "../../elements/IconButton";
import EntityDragLabel from "../../elements/entityDragLabel";
import ImagePackElement from "../../elements/imagePack.element";
import List from "../../elements/list";
import ResizeBar, {useResizable} from "../../elements/resizeBar";
import Collapsible from "../../elements/collapsible";
import {useApp} from "../../app.context";
import {useEffect, useRef, useState} from "react";
import PromptDialog from "../../elements/promptDialog.element";
import {getMetadataDisplayInfo, getDuplicateIds} from "../../utils/metadata.utils";
import PaletteTabButton, {usePaletteTab} from "../../elements/paletteTabButton";
import SelectableLabel from "../../elements/selectable.label";
import {TRIGGER_DESCRIPTIONS} from "../../utils/reward.utils";
import ScoreReadout from "../../elements/scoreReadout.element";
import {effectiveBuildingScore} from "../../utils/scoreFormula";

function BuildingsEditorView() {
    const {state, executeCommand} = useIntegrator();
    const {focusedBuilding, navigateToBuilding} = useApp();

    return (
        <EntityEditor name={'Building'} entities={state.buildings.map((building): EntityProps => {
            return {
                id: building.id,
                statistics: [],
                attributes: building.attributes
            }
        })}
                      attributes={state.buildingAttributes}
                      focusedEntity={focusedBuilding}
                      addNewEntity={(name) => { const c = new AddBuildingCommand(name); executeCommand(c); if (c.newId) navigateToBuilding(c.newId); }}
                      deleteEntity={(id) => executeCommand(new RemoveBuildingCommand(id))}
                      deleteEntityAttribute={(id, attribute) => executeCommand(new RemoveBuildingAttributeCommand(id, attribute))}
                      addEntityAttribute={(id, attribute) => executeCommand(new AddBuildingAttributeCommand(id, attribute))}/>
    )
}

interface EntityEditorProps {
    name: string;
    entities: EntityProps[];
    attributes: Metadata[];
    focusedEntity?: { id: string };
    addNewEntity(name: string): void;
    deleteEntity(id: string): void;
    addEntityAttribute(id: string, attribute: string): void;
    deleteEntityAttribute(id: string, attribute: string): void;
}

export interface EntityProps {
    id: string;
    statistics: StatisticProps[];
    attributes: string[];
}

interface StatisticProps {
    name: string;
    value: number;
    onChange(value: number): void;
}

function EntityEditor(props: EntityEditorProps) {
    const {state} = useIntegrator();
    const {getLocalizationValue} = useApp();
    const [showPrompt, setShowPrompt] = useState(false);
    const dupes = getDuplicateIds(state.buildingAttributes);

    // Resizable palette: a vertical bar drags the width; tabs at the top
    // switch between the Attributes and Units lists. See elements/resizeBar;
    // no collapse buttons here.
    const containerRef = useRef<HTMLDivElement>(null);
    const palette = useResizable({
        storageKey: 'rift.editor.buildingsPaletteWidth',
        axis: 'x', min: 240, invert: true,                      // palette sits right of the bar
        initial: () => Math.round(window.innerWidth * 0.25),    // old fixed 25vw
        // Leave the buildings list at least this wide.
        max: () => (containerRef.current?.getBoundingClientRect().width ?? Infinity) - 320,
    });
    const [paletteTab, setPaletteTab] = usePaletteTab('rift.editor.buildingsPaletteTab', 'Attributes');

    return (
        <div ref={containerRef} className={'layout horizontal'} style={{flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{flexGrow: 1, gap: 10, minWidth: 0}}>
                <List title={'Buildings'} elements={props.entities}
                      headerClassName={'box'}
                      headerAction={<button style={{whiteSpace: 'nowrap'}} onClick={() => setShowPrompt(true)}>{`Create new ${props.name}`}</button>}
                      component={element => <EntityItem entity={element} parent={props} dupes={dupes}/>}
                      search={element => getLocalizationValue(element.id)}
                      keyFor={element => element.id}
                />
                {showPrompt && <PromptDialog
                    title={`New ${props.name}`}
                    placeholder={`Enter ${props.name.toLowerCase()} name...`}
                    existingNames={props.entities.map(e => getLocalizationValue(e.id))}
                    onConfirm={(name) => { props.addNewEntity(name); setShowPrompt(false); }}
                    onCancel={() => setShowPrompt(false)}
                />}
            </div>

            {/* Draggable bar to resize the palette width (drags horizontally). */}
            <ResizeBar axis={'x'} {...palette.handleProps}/>

            <div className={'layout vertical'} style={{width: palette.size, flexShrink: 0}}>
                <div className={'layout vertical gap box'} style={{height: 0, flexGrow: 1, overflowY: 'auto'}}>
                    <div className={'layout horizontal gap'} style={{width: '100%'}}>
                        <PaletteTabButton label={'Attributes'} current={paletteTab} onClick={setPaletteTab}/>
                        <PaletteTabButton label={'Units'} current={paletteTab} onClick={setPaletteTab}/>
                    </div>
                    <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto'}}>
                        {paletteTab === 'Attributes' && [...state.buildingAttributes]
                            .sort((a, b) => getLocalizationValue(a.uid).localeCompare(getLocalizationValue(b.uid)))
                            .map(element => <div key={element.uid}><MetadataDragLabel uid={element.uid} id={element.id} values={element.values} duplicate={dupes.has(element.id)}/></div>)}
                        {paletteTab === 'Units' && [...state.units]
                            .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                            .map(element => <div key={element.id}><EntityDragLabel id={element.id} type={'unit'} navTarget={{kind: 'unit', id: element.id}}/></div>)}
                    </div>
                </div>
            </div>
        </div>
    )
}

function EntityItem(props: { entity: EntityProps, parent: EntityEditorProps, dupes: Set<string> }) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, confirmDelete} = useApp();
    const deleteDisabled = props.parent.entities.length <= 1;

    const building = state.buildings.find(b => b.id === props.entity.id);

    const [open, setOpen] = useState(false);
    // The reward added by the last Add Reward pick — that card mounts expanded
    // so the defaults it was given are visible straight away.
    const [newRewardUid, setNewRewardUid] = useState<string | undefined>(undefined);
    const rowRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (props.parent.focusedEntity?.id === props.entity.id) {
            setOpen(true);
            rowRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [props.parent.focusedEntity, props.entity.id]);

    function checkCanDrop(monitor: DropTargetMonitor<any, void>): boolean {
        if (!monitor.isOver({shallow: true})) {
            return false;
        }

        if (building === undefined) {
            return false;
        }

        const type = monitor.getItemType();
        const item = monitor.getItem();
        switch (type) {
            case 'attribute':
                return !building.attributes?.includes(item.attribute);
            case 'unit':
                return !building.produceableUnits?.includes(item.id);
            default:
                return false;
        }
    }

    const [{canDrop}, drop] = useDrop({
        accept: ['attribute', 'unit'],
        drop: (item: any, monitor) => {
            if (building === undefined || !checkCanDrop(monitor)) {
                return;
            }

            switch (monitor.getItemType()) {
                case 'attribute':
                    building.attributes ??= [];
                    building.attributes.push(item.attribute);
                    break;
                case 'unit':
                    building.produceableUnits ??= [];
                    building.produceableUnits.push(item.id);
                    break;
                default:
                    return;
            }

            executeCommand(new UpdateBuildingCommand(building));
        },
        collect: (monitor) => {
            return {
                canDrop: checkCanDrop(monitor)
            };
        },
    });

    if (building === undefined) {
        return <></>
    }

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center'}}>
            <ImagePackElement id={props.entity.id} level={building.startLevel} size={80}/>
            <LocalizationInput style={{width: 100}} id={props.entity.id}/>
            <BooleanInput value={building.active} onChange={(value) => {
                building.active = value;
                executeCommand(new UpdateBuildingCommand(building));
            }}/>
            <IconButton icon={'trash'} style={{opacity: deleteDisabled ? 0.5 : 1}} onClick={async (e) => {
                e.stopPropagation();
                if (!deleteDisabled && await confirmDelete(getLocalizationValue(props.entity.id), 'building')) {
                    props.parent.deleteEntity(props.entity.id);
                }
            }}/>
        </div>
    );

    return (
        <div ref={(el) => { drop(el); rowRef.current = el; }} className={'box transition'} style={{
            opacity: building.active ? 1 : 0.5,
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <Collapsible title={header} defaultOpen={false} open={open} onOpenChange={setOpen}>
                <div className={'layout vertical gap'} style={{gap: 6, marginTop: 10}}>
                    <p className={'text header'}>Description</p>
                    <LocalizationTextArea id={`${props.entity.id}.description`} style={{minHeight: 50}}/>
                </div>
                <div className={'layout horizontal gap'} style={{gap: 20, marginTop: 10}}>
                    {/* flex: 1 1 0 + minWidth 0 keeps the two columns a stable 50/50
                        regardless of content, so toggling the Construction section
                        (shown only when Construction Turns > 0) doesn't shift the
                        left/right widths. */}
                    <div className={'layout vertical'} style={{flex: '1 1 0', minWidth: 0, gap: 10}}>
                        <div className={'layout vertical gap'}
                             style={{
                                 borderRadius: '5px',
                                 flexGrow: 1, overflowY: 'scroll', height: 0,
                                 paddingRight: 12,
                             }}>
                            <p className={'text header'}>Construction</p>
                            <ConstructionImages building={building}/>
                            <p className={'text header'}>Produceable Units</p>
                            {building?.produceableUnits?.length > 0
                                ? [...building.produceableUnits]
                                    .sort((a, b) => getLocalizationValue(a).localeCompare(getLocalizationValue(b)))
                                    .map((unit, i) => {
                                        return <EntityLabel key={i} id={unit} navTarget={{kind: 'unit', id: unit}} onDelete={() => {
                                            if (building === undefined) return;
                                            building.produceableUnits = building.produceableUnits.filter(t => t !== unit);
                                            executeCommand(new UpdateBuildingCommand(building));
                                        }}/>
                                    })
                                : <p className={'muted-text'}>None</p>
                            }
                            <p className={'text header'}>Attributes</p>
                            {props.entity.attributes.length > 0
                                ? [...props.entity.attributes]
                                    .sort((a, b) => getLocalizationValue(a).localeCompare(getLocalizationValue(b)))
                                    .map((uid, i) => {
                                        const meta = state.buildingAttributes?.find(m => m.uid === uid);
                                        const dup = props.dupes.has(meta?.id ?? '');
                                        const {suffix, tooltip} = getMetadataDisplayInfo(meta, dup, getLocalizationValue);
                                        return <div key={i} title={tooltip}><MetadataLabel uid={uid} suffix={suffix} onDelete={() => {
                                            props.parent.deleteEntityAttribute(props.entity.id, uid);
                                        }}/></div>;
                                    })
                                : <p className={'muted-text'}>None</p>
                            }
                            <p className={'text header'}>Resource Variants</p>
                            <ResourceVariants building={building}/>
                        </div>
                    </div>
                    <div className={'layout vertical'} style={{flex: '1 1 0', minWidth: 0, gap: 20}}>
                        <p className={'text header'}>Statistics</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel 
                                              style={{flexGrow: 'unset'}}
                                              field={'building.price'}
                                              value={building.price}
                                              onChange={(value: number) => {
                                                  building.price = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel 
                                              style={{flexGrow: 'unset'}}
                                              field={'building.score'}
                                              value={building.score}
                                              onChange={(value: number) => {
                                                  building.score = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            {/* The field stays the designer's; the row under it is what the Building (or,
                                for a city, the City) Score Formula on the General tab makes of it. */}
                            <ScoreReadout effective={effectiveBuildingScore(state, building)}/>
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.visionRange'}
                                              value={building.properties.visionRange}
                                              onChange={(value: number) => {
                                                  building.properties.visionRange = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.territoryRange'}
                                              value={building.properties.territoryRange}
                                              onChange={(value: number) => {
                                                  building.properties.territoryRange = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.startingSlots'}
                                              value={building.properties.startingSlots}
                                              onChange={(value: number) => {
                                                  building.properties.startingSlots = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            {/* Combat structure. Health is the switch: at 0 the building
                                cannot be attacked and the four below do nothing, which is
                                every ordinary building. All read `?? 0` because a branch
                                authored before these fields existed has no such keys. */}
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.health'}
                                              value={building.properties.health ?? 0}
                                              onChange={(value: number) => {
                                                  building.properties.health = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.attack'}
                                              value={building.properties.attack ?? 0}
                                              onChange={(value: number) => {
                                                  building.properties.attack = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.defense'}
                                              value={building.properties.defense ?? 0}
                                              onChange={(value: number) => {
                                                  building.properties.defense = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.range'}
                                              value={building.properties.range ?? 0}
                                              onChange={(value: number) => {
                                                  building.properties.range = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.properties.regeneration'}
                                              value={building.properties.regeneration ?? 0}
                                              onChange={(value: number) => {
                                                  building.properties.regeneration = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.maxPerCity'}
                                              value={building.maxPerCity}
                                              onChange={(value: number) => {
                                                  building.maxPerCity = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.maxPerPlayer'}
                                              value={building.maxPerPlayer}
                                              onChange={(value: number) => {
                                                  building.maxPerPlayer = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <SelectableLabel field={'building.countsAs'} style={{flexGrow: 'unset'}}
                                             value={building.countsAs ?? ''}
                                             placeholder={'Self'}
                                             options={state.buildings.filter(b => b.id !== building.id).map(b => b.id)}
                                             onChange={(value) => {
                                                 building.countsAs = value;
                                                 executeCommand(new UpdateBuildingCommand(building));
                                             }}
                            />
                        </div>
                        <p className={'text header'}>Levels</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.startLevel'}
                                              value={building.startLevel}
                                              onChange={(value: number) => {
                                                  building.startLevel = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel style={{flexGrow: 'unset'}}
                                              field={'building.maxLevel'}
                                              value={building.maxLevel}
                                              onChange={(value: number) => {
                                                  building.maxLevel = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel 
                                              step={'1'}
                                              style={{flexGrow: 'unset'}}
                                              field={'building.constructionTurns'}
                                              value={building.constructionTurns ?? 0}
                                              onChange={(value: number) => {
                                                  building.constructionTurns = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                        </div>
                        <p className={'text header'}>Rewards</p>
                        <div className={'layout vertical gap'} style={{gap: 6}}>
                            {(building.passiveRewards ?? []).map(reward => (
                                <PassiveRewardCard key={reward.uid} buildingId={building.id} reward={reward}
                                                   defaultOpen={reward.uid === newRewardUid}/>
                            ))}
                            <AddRewardSelect onAdd={(kind) => {
                                const command = new AddBuildingPassiveRewardCommand(building.id, kind);
                                executeCommand(command);
                                setNewRewardUid(command.newUid);
                            }}/>
                        </div>
                    </div>
                </div>
            </Collapsible>
        </div>
    )
}

// "Add Reward" picks the trigger up front instead of always adding an OnPlace
// reward the user then has to retype — the trigger is what a reward is, and the
// rest of the card depends on which one is chosen. Same shape as the Create New
// dropdown in the attribute columns: the placeholder stays selected (value=''),
// so the control self-resets for the next add.
function AddRewardSelect(props: { onAdd(kind: TriggerSpec['kind']): void }) {
    return (
        <select style={{width: '100%'}} value={''}
                onChange={(e) => { if (e.target.value) props.onAdd(e.target.value as TriggerSpec['kind']); }}>
            <option value={''} disabled hidden>Add Reward…</option>
            {TriggerKinds.map(kind => (
                <option key={kind} value={kind} title={TRIGGER_DESCRIPTIONS[kind]}>{kind}</option>
            ))}
        </select>
    );
}

function MetadataDragLabel(props: { uid: string, id: string, values?: { [key: string]: number }, duplicate?: boolean }) {
    const {getLocalizationValue} = useApp();
    const [{opacity}, drag] = useDrag({
        type: 'attribute',
        item: {
            attribute: props.uid,
        },
        collect: (monitor) => {
            const isDragging = monitor.isDragging();
            const opacity = isDragging ? 0.5 : 1;
            return {
                opacity: opacity,
            };
        },
    });

    const meta = props.id ? {id: props.id, values: props.values ?? {}} as any : undefined;
    const {suffix, tooltip} = getMetadataDisplayInfo(meta, props.duplicate ?? false, getLocalizationValue);

    return (
        <div ref={drag} style={{opacity}} title={tooltip}>
            <MetadataLabel uid={props.uid} suffix={suffix}/>
        </div>
    );
}

export default BuildingsEditorView;